import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'
import { track } from '../../../family-design/analytics'

interface AuthUser {
  userId: string
  accountId: string
  email: string
  displayName: string
  role: string
  isSystemAdmin?: boolean
}

interface AuthCtx {
  user: AuthUser | null
  loading: boolean
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  logout: () => Promise<void>
}

const Ctx = createContext<AuthCtx | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  useEffect(() => {
    apiClient.get<AuthUser>('/auth/me')
      .then(r => { if (r.success) setUser(r.data!) })
      .finally(() => setLoading(false))
  }, [])

  const login = async (email: string, password: string, rememberMe?: boolean) => {
    const r = await apiClient.post<AuthUser>('/auth/login', { email, password, rememberMe })
    if (!r.success) throw new Error(r.error || 'ログインに失敗しました')
    // 前のアカウントのキャッシュ（設定など）を持ち越さない。残っていると
    // アカウント A の設定を B として保存し得る（audit C9）
    queryClient.clear()
    setUser(r.data!)
    track('login', { method: 'email' })
  }

  const logout = async () => {
    // POST が失敗しても必ずローカル状態はクリアする（short と同型）。
    // setUser(null) で App.tsx の Protected が /login へリダイレクトするため、
    // 呼び出し側での明示 nav('/login') は不要（await 前に nav すると
    // login→dashboard→login のバウンスが起きる — audit MEDIUM）
    try {
      await apiClient.post('/auth/logout')
    } finally {
      setUser(null)
      // ログアウト後に別アカウントでログインしても前のデータが見えないよう全キャッシュを捨てる（audit C9）
      queryClient.clear()
    }
  }

  return <Ctx.Provider value={{ user, loading, login, logout }}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
